import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, Plus, Pencil, Trash2, Rocket, Wrench, Bug, Loader2, EyeOff, Lightbulb, MessageSquare, Clock, CheckCircle2, Code2, ArrowUpCircle } from "lucide-react";
import { motion } from "framer-motion";

const typeIcons: Record<string, any> = {
  feature: Rocket,
  improvement: Wrench,
  bugfix: Bug,
};

const typeColors: Record<string, string> = {
  feature: "bg-primary/10 text-primary border-primary/20",
  improvement: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  bugfix: "bg-amber-500/10 text-amber-600 border-amber-500/20",
};

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  pending: { label: "Pendente", color: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20", icon: Clock },
  approved: { label: "Aprovada", color: "bg-green-500/10 text-green-600 border-green-500/20", icon: CheckCircle2 },
  development: { label: "Desenvolvimento", color: "bg-blue-500/10 text-blue-600 border-blue-500/20", icon: Code2 },
  implemented: { label: "Implementada", color: "bg-primary/10 text-primary border-primary/20", icon: ArrowUpCircle },
  rejected: { label: "Rejeitada", color: "bg-destructive/10 text-destructive border-destructive/20", icon: Trash2 },
};

interface ChangelogFormData {
  version: string;
  title: string;
  description: string;
  type: string;
  visibility: string;
  is_published: boolean;
  release_date: string;
}

const AdminChangelogTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [changelogs, setChangelogs] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Suggestions state
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<any>(null);
  const [publicComment, setPublicComment] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const [formData, setFormData] = useState<ChangelogFormData>({
    version: "",
    title: "",
    description: "",
    type: "feature",
    visibility: "public",
    is_published: true,
    release_date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    loadChangelogs();
    loadSuggestions();
  }, []);

  const loadChangelogs = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("changelogs")
      .select("*")
      .order("release_date", { ascending: false })
      .order("sort_order", { ascending: true });
    setChangelogs(data || []);
    setLoading(false);
  };

  const loadSuggestions = async () => {
    setLoadingSuggestions(true);
    const { data } = await supabase
      .from("feature_requests")
      .select("*")
      .order("created_at", { ascending: false });
    setSuggestions(data || []);
    setLoadingSuggestions(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        const { error } = await supabase.from("changelogs").update(formData).eq("id", editingId);
        if (error) throw error;
        toast({ title: "Changelog atualizado com sucesso!" });
      } else {
        const { error } = await supabase.from("changelogs").insert(formData);
        if (error) throw error;
        toast({ title: "Changelog criado com sucesso!" });
      }
      setDialogOpen(false);
      resetForm();
      loadChangelogs();
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (changelog: any) => {
    setEditingId(changelog.id);
    setFormData({
      version: changelog.version,
      title: changelog.title,
      description: changelog.description,
      type: changelog.type,
      visibility: changelog.visibility,
      is_published: changelog.is_published,
      release_date: changelog.release_date,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este changelog?")) return;
    try {
      const { error } = await supabase.from("changelogs").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Changelog excluído com sucesso!" });
      loadChangelogs();
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      version: "",
      title: "",
      description: "",
      type: "feature",
      visibility: "public",
      is_published: true,
      release_date: new Date().toISOString().split("T")[0],
    });
  };

  const handleUpdateSuggestionStatus = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from("feature_requests")
        .update({ status: newStatus })
        .eq("id", id);
      if (error) throw error;
      toast({ title: `Status atualizado para "${statusConfig[newStatus]?.label || newStatus}"` });
      loadSuggestions();
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    }
  };

  const handleSaveComment = async () => {
    if (!selectedSuggestion) return;
    setSavingComment(true);
    try {
      const { error } = await supabase
        .from("feature_requests")
        .update({ public_comment: publicComment })
        .eq("id", selectedSuggestion.id);
      if (error) throw error;
      toast({ title: "Comentário salvo com sucesso!" });
      setCommentDialogOpen(false);
      setSelectedSuggestion(null);
      setPublicComment("");
      loadSuggestions();
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSavingComment(false);
    }
  };

  const openCommentDialog = (suggestion: any) => {
    setSelectedSuggestion(suggestion);
    setPublicComment(suggestion.public_comment || "");
    setCommentDialogOpen(true);
  };

  const filteredSuggestions = statusFilter === "all"
    ? suggestions
    : suggestions.filter((s) => s.status === statusFilter);

  const groupedChangelogs = changelogs.reduce((acc, log) => {
    if (!acc[log.version]) acc[log.version] = [];
    acc[log.version].push(log);
    return acc;
  }, {} as Record<string, any[]>);

  const pendingCount = suggestions.filter((s) => s.status === "pending").length;

  if (loading && loadingSuggestions) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-primary-foreground" />
        </div>
        <div>
          <h2 className="text-2xl font-bold font-display">Changelog & Sugestões</h2>
          <p className="text-sm text-muted-foreground">
            Gerencie atualizações e sugestões dos usuários
          </p>
        </div>
      </div>

      <Tabs defaultValue="changelog" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="changelog" className="gap-2">
            <Sparkles className="h-4 w-4" />
            Changelog
          </TabsTrigger>
          <TabsTrigger value="suggestions" className="gap-2">
            <Lightbulb className="h-4 w-4" />
            Sugestões
            {pendingCount > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 min-w-5 text-xs px-1.5">
                {pendingCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ===== CHANGELOG TAB ===== */}
        <TabsContent value="changelog" className="space-y-6 mt-6">
          <div className="flex justify-end">
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" />
                  Nova Atualização
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingId ? "Editar Atualização" : "Nova Atualização"}</DialogTitle>
                  <DialogDescription>Adicione uma nova entrada no changelog</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="version">Versão</Label>
                      <Input id="version" value={formData.version} onChange={(e) => setFormData({ ...formData, version: e.target.value })} placeholder="ex: 2.1.0" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="release_date">Data de Lançamento</Label>
                      <Input id="release_date" type="date" value={formData.release_date} onChange={(e) => setFormData({ ...formData, release_date: e.target.value })} required />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="title">Título</Label>
                    <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Nome da funcionalidade ou correção" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Descrição</Label>
                    <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Descreva a atualização em detalhes" rows={3} required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo</Label>
                      <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="feature">✨ Nova Funcionalidade</SelectItem>
                          <SelectItem value="improvement">🔧 Melhoria</SelectItem>
                          <SelectItem value="bugfix">🐛 Correção de Bug</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Visibilidade</Label>
                      <Select value={formData.visibility} onValueChange={(value) => setFormData({ ...formData, visibility: value })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public">👁️ Público</SelectItem>
                          <SelectItem value="admin_only">🔒 Apenas Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="is_published" checked={formData.is_published} onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })} className="rounded border-input" />
                    <Label htmlFor="is_published" className="cursor-pointer">Publicar imediatamente</Label>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="flex-1">Cancelar</Button>
                    <Button type="submit" disabled={submitting} className="flex-1">
                      {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Salvando...</> : editingId ? "Atualizar" : "Criar"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-6">
            {Object.entries(groupedChangelogs).map(([version, logs]: [string, any[]]) => (
              <motion.div key={version} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl font-display">Versão {version}</CardTitle>
                      <Badge variant="outline">{new Date(logs[0].release_date).toLocaleDateString("pt-BR")}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {logs.map((log: any) => {
                      const Icon = typeIcons[log.type] || Rocket;
                      return (
                        <div key={log.id} className="flex gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group">
                          <div className={`w-8 h-8 rounded-lg ${typeColors[log.type]} flex items-center justify-center shrink-0`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-medium">{log.title}</h4>
                                  {log.visibility === "admin_only" && (
                                    <Badge variant="secondary" className="text-xs gap-1"><EyeOff className="h-3 w-3" />Admin</Badge>
                                  )}
                                  {!log.is_published && <Badge variant="outline" className="text-xs">Rascunho</Badge>}
                                </div>
                                <p className="text-sm text-muted-foreground">{log.description}</p>
                              </div>
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button size="sm" variant="ghost" onClick={() => handleEdit(log)} className="h-8 w-8 p-0"><Pencil className="h-3.5 w-3.5" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDelete(log.id)} className="h-8 w-8 p-0 text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
            {changelogs.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center">
                    <Sparkles className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Nenhum changelog criado</h3>
                  <p className="text-sm text-muted-foreground">Comece criando a primeira atualização</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ===== SUGGESTIONS TAB ===== */}
        <TabsContent value="suggestions" className="space-y-6 mt-6">
          {/* Status filter */}
          <div className="flex flex-wrap gap-2">
            {[
              { value: "all", label: "Todas", count: suggestions.length },
              { value: "pending", label: "Pendentes", count: suggestions.filter((s) => s.status === "pending").length },
              { value: "approved", label: "Aprovadas", count: suggestions.filter((s) => s.status === "approved").length },
              { value: "development", label: "Desenvolvimento", count: suggestions.filter((s) => s.status === "development").length },
              { value: "implemented", label: "Implementadas", count: suggestions.filter((s) => s.status === "implemented").length },
            ].map((filter) => (
              <Button
                key={filter.value}
                variant={statusFilter === filter.value ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter(filter.value)}
                className="gap-1.5"
              >
                {filter.label}
                <Badge variant={statusFilter === filter.value ? "secondary" : "outline"} className="h-5 min-w-5 text-xs px-1.5">
                  {filter.count}
                </Badge>
              </Button>
            ))}
          </div>

          {loadingSuggestions ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            </div>
          ) : filteredSuggestions.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center">
                  <Lightbulb className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Nenhuma sugestão encontrada</h3>
                <p className="text-sm text-muted-foreground">Não há sugestões com este filtro</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredSuggestions.map((suggestion) => {
                const status = statusConfig[suggestion.status] || statusConfig.pending;
                const StatusIcon = status.icon;
                return (
                  <motion.div key={suggestion.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    <Card className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-lg ${status.color} flex items-center justify-center shrink-0`}>
                            <StatusIcon className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-semibold">{suggestion.title}</h4>
                                  <Badge className={`text-xs ${status.color}`}>{status.label}</Badge>
                                  <Badge variant="outline" className="text-xs">{suggestion.category}</Badge>
                                  {suggestion.votes_count > 0 && (
                                    <Badge variant="secondary" className="text-xs gap-1">
                                      👍 {suggestion.votes_count}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">{suggestion.description}</p>
                              </div>
                            </div>

                            {suggestion.public_comment && (
                              <div className="bg-muted/50 rounded-lg p-3 border border-border/50">
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                                  <MessageSquare className="h-3 w-3" />
                                  Resposta da equipe
                                </div>
                                <p className="text-sm">{suggestion.public_comment}</p>
                              </div>
                            )}

                            <div className="flex items-center gap-2 pt-1">
                              <Select
                                value={suggestion.status}
                                onValueChange={(value) => handleUpdateSuggestionStatus(suggestion.id, value)}
                              >
                                <SelectTrigger className="w-[180px] h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">⏳ Pendente</SelectItem>
                                  <SelectItem value="approved">✅ Aprovada</SelectItem>
                                  <SelectItem value="development">💻 Desenvolvimento</SelectItem>
                                  <SelectItem value="implemented">🚀 Implementada</SelectItem>
                                  <SelectItem value="rejected">❌ Rejeitada</SelectItem>
                                </SelectContent>
                              </Select>

                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1.5 text-xs"
                                onClick={() => openCommentDialog(suggestion)}
                              >
                                <MessageSquare className="h-3.5 w-3.5" />
                                {suggestion.public_comment ? "Editar Comentário" : "Comentar"}
                              </Button>

                              <span className="text-xs text-muted-foreground ml-auto">
                                {new Date(suggestion.created_at).toLocaleDateString("pt-BR")}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Comentário Público</DialogTitle>
            <DialogDescription>
              Este comentário será visível para todos os usuários na sugestão "{selectedSuggestion?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Textarea
              value={publicComment}
              onChange={(e) => setPublicComment(e.target.value)}
              placeholder="Escreva uma resposta pública para esta sugestão..."
              rows={4}
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCommentDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleSaveComment} disabled={savingComment} className="flex-1">
                {savingComment ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Salvando...</> : "Salvar Comentário"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminChangelogTab;
